import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider } from './contexts/AuthContext';
import { ThemeProvider, createTheme } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';
import { LoadingProvider } from './contexts/LoadingContext';
import { ErrorProvider } from './contexts/ErrorContext';

// Components
import Layout from './components/Layout';

// Pages
import Home from './pages/Home';
import QuizLobby from './pages/QuizLobby';
import QuizGame from './pages/QuizGame';
import QuizDetails from './pages/QuizDetails';
import Results from './pages/Results';
import Profile from './pages/Profile';
import ProfileEdit from './pages/ProfileEdit';
import AvatarEdit from './components/AvatarEdit';
import MyQuizzes from './pages/MyQuizzes';
import QuizEdit from './pages/QuizEdit';

// Theme erstellen
const theme = createTheme({
  palette: {
    mode: 'dark',
    background: {
      default: '#0a192f',
      paper: 'rgba(10, 25, 47, 0.8)'
    },
    primary: {
      main: '#cb7e00',
      light: '#e69500',
      dark: '#a66600'
    },
    secondary: {
      main: '#cb7e00',
      light: '#e69500',
      dark: '#a66600'
    },
    text: {
      primary: 'rgba(255, 255, 255, 0.95)',
      secondary: 'rgba(255, 255, 255, 0.7)'
    }
  },
  typography: {
    h1: {
      fontSize: '5rem',
      fontWeight: 900,
      '@media (min-width: 961px)': {
        fontSize: '5rem'
      }
    }
  },
  components: {
    MuiAppBar: {
      styleOverrides: {
        root: {
          background: 'linear-gradient(180deg, rgb(0 0 0 / 60%) 0%, rgb(0 0 0 / 45%) 100%)',
          backdropFilter: 'blur(8px)',
          borderBottom: '1px solid var(--glass-border)',
          boxShadow: 'var(--glass-shadow)'
        }
      }
    },
    MuiTypography: {
      styleOverrides: {
        h1: {
          fontSize: '5rem !important',
          '@media (min-width: 961px)': {
            fontSize: '5rem !important'
          }
        }
      }
    },
    MuiTextField: {
      styleOverrides: {
        root: {
          '& .MuiOutlinedInput-root': {
            backgroundColor: 'rgba(28, 39, 60, 0.4)',
            backdropFilter: 'blur(8px)',
            borderRadius: '8px',
            transition: 'all 0.2s ease-in-out',
            '& fieldset': {
              borderColor: 'rgba(255, 255, 255, 0.1)',
              transition: 'all 0.2s ease-in-out'
            },
            '&:hover': {
              backgroundColor: 'rgba(28, 39, 60, 0.5)',
              '& fieldset': {
                borderColor: 'rgba(255, 255, 255, 0.2)'
              }
            },
            '&.Mui-focused': {
              backgroundColor: 'rgba(28, 39, 60, 0.6)',
              '& fieldset': {
                borderColor: '#cb7e00',
                borderWidth: '2px'
              }
            }
          },
          '& .MuiInputLabel-root': {
            color: 'rgba(255, 255, 255, 0.7)',
            '&.Mui-focused': {
              color: '#cb7e00'
            }
          },
          '& .MuiOutlinedInput-input': {
            color: 'rgba(255, 255, 255, 0.95)',
            '&:-webkit-autofill': {
              WebkitBoxShadow: '0 0 0 1000px rgba(28, 39, 60, 0.4) inset',
              WebkitTextFillColor: 'rgba(255, 255, 255, 0.95)',
              caretColor: 'rgba(255, 255, 255, 0.95)',
              borderRadius: '8px',
              WebkitBackgroundClip: 'text'
            },
            '&:-webkit-autofill:hover': {
              WebkitBoxShadow: '0 0 0 1000px rgba(28, 39, 60, 0.5) inset'
            },
            '&:-webkit-autofill:focus': {
              WebkitBoxShadow: '0 0 0 1000px rgba(28, 39, 60, 0.6) inset'
            }
          }
        }
      }
    },
    MuiButton: {
      styleOverrides: {
        root: {
          borderRadius: '8px',
          textTransform: 'none',
          '&.MuiButton-contained': {
            backgroundColor: '#cb7e00',
            '&:hover': {
              backgroundColor: '#e69500'
            }
          }
        }
      }
    },
    MuiAlert: {
      styleOverrides: {
        root: {
          borderRadius: '8px',
          backdropFilter: 'blur(8px)'
        }
      }
    },
    MuiBox: {
      styleOverrides: {
        root: {
          '&.lobby-controls > div:first-of-type': {
            padding: 0
          }
        }
      }
    },
    MuiAvatar: {
      styleOverrides: {
        root: {
          backgroundColor: 'var(--color-background-light)',
          color: 'var(--color-secondary)'
        }
      }
    }
  }
});

// Router-Konfiguration mit Future Flags
const router = {
  future: {
    v7_startTransition: true,
    v7_relativeSplatPath: true
  }
};

function App() {
  return (
    <AuthProvider>
      <LoadingProvider>
        <ErrorProvider>
          <ThemeProvider theme={theme}>
            <CssBaseline />
            <Router {...router}>
              <Routes>
                <Route element={<Layout />}>
                  <Route path="/" element={<Home />} />
                  <Route path="/quiz/:quizId" element={<QuizDetails />} />
                  <Route path="/quiz/create" element={<QuizEdit />} />
                  <Route path="/quiz/edit/:quizId" element={<QuizEdit />} />
                  <Route path="/my-quizzes" element={<MyQuizzes />} />
                  <Route path="/lobby/:lobbyId" element={<QuizLobby />} />
                  <Route path="/game/:gameId" element={<QuizGame />} />
                  <Route path="/results/:gameId" element={<Results />} />
                  <Route path="/profil/:profilePath" element={<Profile />} />
                  <Route path="/profil/edit" element={<ProfileEdit />} />
                  <Route path="/avatar/edit" element={<AvatarEdit />} />
                  <Route path="*" element={<Navigate to="/" replace />} />
                </Route>
              </Routes>
            </Router>
          </ThemeProvider>
        </ErrorProvider>
      </LoadingProvider>
    </AuthProvider>
  );
}

export default App;
